import { BodyPrimary, H5 } from '@liveauctioneers/caterwaul-components/lib/Text/Text';
import { difference } from 'es-toolkit';
import { getSearchUiState } from '@/redux/modules/search/search.selectors';
import { isSimilarItemsPage } from '@/utils/urls';
import { isUserLoggedIn } from '@/redux/modules/account/user/user.selectors';
import { SearchPageName } from '@/types/search/searchQuery';
import { SearchParams as SearchParamsType } from '@/types/SaveSearch';
import { SearchSortAndDirection } from '@/types/search/enums/sortTypes';
import { submitRecentSearch } from '@/redux/modules/saveSearch';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { useLocation } from 'react-router-dom';
import { waitToDispatch } from '@/utils/waitToDispatch/waitToDispatch';
import CompleteAccountBanner from '@/components/CompleteAccountBanner/CompleteAccountBanner';
import FacetTypes from '@/types/search/enums/facetTypes';
import FilterBar from '@/pages/Search/Components/FilterBar/FilterBar';
import isEqual from 'react-fast-compare';
import NewSearchSteps from '../NewSearchSteps';
import React, { useEffect, useId } from 'react';
import ResultsCount from '@/pages/Search/Components/ResultsCount/ResultsCount';
import SaveSearchContainer from '@/components/SaveSearch/SaveSearchContainer';
import SearchError from '@/components/SearchError/SearchError';
import SearchFacets from '@/components/SearchFacets/SearchFacets';
import SearchHeader from '../SearchHeader';
import SearchPageHeaderMeta from '../Components/meta/SearchPageHeaderMeta';
import SearchResults from '@/components/SearchResults/SearchResults';
import SearchSort from '@/components/SearchResults/SearchSort';
import SearchStatusTabs from '@/pages/Search/CategorySearch/components/SearchStatusTabs/SearchStatusTabs';
import styled, { useTheme } from 'styled-components';
import Theme from '@liveauctioneers/caterwaul-components/lib/theme/theme';
import Throbber from '@liveauctioneers/caterwaul-components/lib/Throbber/Throbber';
import UnfollowSearch, { UnfollowSearchTypes } from '@/components/UnfollowSearch/UnfollowSearch';
import useIsWiderThan from '@/hooks/useIsWiderThan';

export type SearchPageProps = {
    archivedSearch: boolean;
    baseUrl: string;
    changePageAndNavigate: Function;
    changePageSizeAndNavigate: Function;
    clearAllParamsExceptCategories: (event: any) => void;
    currentStateCode?: string;
    distanceSelected: (countryName: string, zipCode: string, range: number, measurement: string) => void;
    excludedFacets: string[];
    facetOptionChecked: (facetName: string, value: string, isChecked: boolean) => void;
    facets: any[];
    filterTerms: string[];
    houses?: any[];
    isLoading: boolean;
    liveItemsFound?: number;
    onStateChange?: (stateCode: string | null) => void;
    page: number;
    pageLabel?: string;
    pageName: string;
    pageSize: number;
    pathname: string;
    queryParams: any;
    rangeEntered: Function;
    removeCountryAndDistance: (event?: any) => void;
    removeFacetById: Function;
    removePublishedDate: (event: any) => void;
    removeQueryParams?: Function;
    removeSaleDates?: (event: any) => void;
    replaceCountry: (countryId: string) => void;
    saveSearchParams: SearchParamsType;
    searchFailed: boolean;
    searchQuery: any;
    searchSubmitted: boolean;
    selectedCreator?: any;
    soldItemsFound?: number;
    sortOptions: SearchSortAndDirection[];
    sortSelected: (sortOption: any) => void;
    taxonomyAllSelected: Function;
    taxonomyParentSelected: Function;
    toggleFilterTerm: (filterTerm: string) => void;
    totalItemsFound: number;
    totalPages: number;
    useSimilarItemsCard?: boolean;
    whiteLabel?: boolean;
};

const SearchPage = ({
    archivedSearch,
    baseUrl,
    changePageAndNavigate,
    changePageSizeAndNavigate,
    clearAllParamsExceptCategories,
    currentStateCode,
    distanceSelected,
    excludedFacets,
    facetOptionChecked,
    facets,
    filterTerms,
    houses,
    isLoading,
    liveItemsFound,
    onStateChange,
    page,
    rangeEntered,
    removeCountryAndDistance,
    removeFacetById,
    removePublishedDate,
    removeSaleDates,
    replaceCountry,
    saveSearchParams,
    searchFailed,
    searchQuery,
    searchSubmitted,
    soldItemsFound,
    sortSelected,
    taxonomyAllSelected,
    taxonomyParentSelected,
    toggleFilterTerm,
    totalItemsFound,
    whiteLabel,
}: SearchPageProps) => {
    const dispatch = useAppDispatch();
    const location = useLocation();
    const { pathname, search } = location;
    const similarItemsPage = isSimilarItemsPage(pathname);

    useEffect(() => {
        const url = `${pathname}${search}`;
        waitToDispatch(submitRecentSearch, dispatch, {
            saveSearchParams,
            url,
        });
        // only want to save once when URL changes, ignore other params
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pathname, search]);

    const selectId = useId();
    const { sort: sortOptions } = useAppSelector(getSearchUiState);
    const { distance, publishDate, ranges, saleDate, searchTerm, sort } = searchQuery;
    const { max: saleDateMax, min: saleDateMin } = saleDate || {};
    const {
        breakpoints: { tabletNarrowWidthNum },
    } = useTheme() as typeof Theme;
    const isMobileView = !useIsWiderThan(tabletNarrowWidthNum);
    const isLoggedIn = useAppSelector(isUserLoggedIn);
    const firstHouse = houses?.[0];
    const showNoResults = totalItemsFound === 0 && !searchFailed && !isLoading;
    const shouldShowContent = !isLoading;
    const allSearchParams: SearchParamsType = {
        ...saveSearchParams,
        distance,
        facets,
        publishDate,
        ranges,
    };

    if (search.includes('newTerm')) {
        return <NewSearchSteps />;
    }

    // this logic is based around the search filter chips. Someday, we should consolidate it all into one piece
    // but it is too out-of-scope for SW-1066.
    const filtersWithoutCategories = facets.filter((facet) => facet.id !== FacetTypes.CATEGORY_ID);
    const hasSelectedFacets = filtersWithoutCategories
        .map((facet): boolean => {
            if (facet.options) {
                return facet.options.some(({ selected }) => selected);
            } else if (facet.categories) {
                return facet.categories.facets?.some(({ selected }) => selected);
            }
            return false;
        })
        .some((isValid) => isValid);
    const hasFilterTerms = filterTerms?.length > 0;
    const hasDistanceFilter = Boolean(distance?.space) && Boolean(distance?.zipCode);
    const hasRangesFilter = Boolean(ranges?.currentBid) || Boolean(ranges?.estimate) || Boolean(ranges?.hammerPrice);
    // In SearchFilterChips, the published date is typed as any and accesses the 'min' prop, so I don't think this is actually a number?
    const hasPublishDateFilter = Boolean((publishDate as any)?.min);
    const hasSaleDateFilters = Boolean(saleDateMin) || Boolean(saleDateMax);
    const empty =
        !hasSelectedFacets &&
        !hasFilterTerms &&
        !hasDistanceFilter &&
        !hasRangesFilter &&
        !hasPublishDateFilter &&
        !hasSaleDateFilters;

    const uniqueSortOptions = sortOptions.includes(SearchSortAndDirection.LotNumberLowest)
        ? difference(sortOptions, [SearchSortAndDirection.LotNumberLowest])
        : sortOptions;
    const preparedSearchSort = (
        <SearchSort
            archivedSearch={archivedSearch}
            instanceId={selectId}
            key={archivedSearch ? 'archive' : 'online'}
            sort={sort}
            sortOptions={uniqueSortOptions}
            sortSelected={sortSelected}
        />
    );

    const preparedFilterBar = (
        <FilterBar
            distance={distance}
            empty={empty}
            facetOptionChecked={facetOptionChecked}
            facets={facets}
            filterTerms={filterTerms}
            publishDate={publishDate}
            ranges={ranges}
            removeCountryAndDistance={removeCountryAndDistance}
            removeFacetById={removeFacetById}
            removePublishedDate={removePublishedDate}
            removeSaleDates={removeSaleDates}
            saleDateMax={saleDateMax}
            saleDateMin={saleDateMin}
            taxonomyAllSelected={taxonomyAllSelected}
            toggleFilterTerm={toggleFilterTerm}
        />
    );

    const preparedSearchFacets = (
        <SearchFacets
            clearAllParamsExceptCategories={clearAllParamsExceptCategories}
            currentStateCode={currentStateCode}
            distanceSelected={distanceSelected}
            empty={empty}
            excludedFacets={excludedFacets}
            facetOptionChecked={facetOptionChecked}
            filterBar={preparedFilterBar}
            isMobileView={isMobileView}
            onStateChange={onStateChange}
            pageName={archivedSearch ? SearchPageName.SOLD_SEARCH : SearchPageName.SEARCH}
            rangeEntered={rangeEntered}
            removeCountryAndDistance={removeCountryAndDistance}
            replaceCountry={replaceCountry}
            sortFilter={preparedSearchSort}
            taxonomyParentSelected={taxonomyParentSelected}
            toggleFilterTerm={toggleFilterTerm}
            userIsLoggedIn={isLoggedIn}
        />
    );

    return (
        <StyledSearchPage>
            <CompleteAccountBanner />
            <UnfollowSearch searchType={UnfollowSearchTypes.search} />
            <SearchPageHeaderMeta
                baseUrl={baseUrl}
                page={page}
                searchTerm={searchTerm}
            />
            {searchFailed ? (
                <SearchError />
            ) : (
                <>
                    {shouldShowContent && (
                        <>
                            <SearchHeader
                                allSearchParams={allSearchParams}
                                archivedSearch={archivedSearch}
                                firstHouse={firstHouse}
                                isMobileView={isMobileView}
                                liveItemsFound={liveItemsFound}
                                saveSearchParams={saveSearchParams}
                                searchTerm={searchTerm}
                                showNoResults={showNoResults}
                                whiteLabel={whiteLabel}
                            />
                            {!showNoResults && (
                                <>
                                    {!similarItemsPage && (
                                        <TabsContainer>
                                            <SearchStatusTabs
                                                activeTab={archivedSearch ? 'archive' : 'online'}
                                                isKeywordSearch
                                                keyword={searchTerm}
                                            />
                                        </TabsContainer>
                                    )}
                                    <ResultsRow>
                                        {isMobileView ? (
                                            <>
                                                <ResultsCountMobile>
                                                    <ResultsCount
                                                        isPriceResults={archivedSearch}
                                                        totalFound={archivedSearch ? soldItemsFound : totalItemsFound}
                                                    />
                                                </ResultsCountMobile>
                                                {preparedSearchFacets}
                                                <FloatingSaveButton>
                                                    <SaveButton>
                                                        <SaveSearchContainer
                                                            allSearchParams={allSearchParams}
                                                            className="searchOptionsSaveButton"
                                                            saveSearchParams={saveSearchParams}
                                                            tooltipPlacement="bottom"
                                                        />
                                                    </SaveButton>
                                                </FloatingSaveButton>
                                            </>
                                        ) : (
                                            <>
                                                <ResultsCountDesktop $hasSortOptions={uniqueSortOptions.length > 0}>
                                                    <ResultsCount
                                                        isPriceResults={archivedSearch}
                                                        totalFound={archivedSearch ? soldItemsFound : totalItemsFound}
                                                    />
                                                </ResultsCountDesktop>
                                                {preparedSearchSort}
                                            </>
                                        )}
                                    </ResultsRow>
                                </>
                            )}
                        </>
                    )}
                    <StyledSearchBody>
                        {!isMobileView && preparedSearchFacets}
                        <StyledResultsWrapper>
                            {preparedFilterBar}
                            {totalItemsFound > 0 && (
                                <SearchResults
                                    changePageAndNavigate={changePageAndNavigate}
                                    changePageSizeAndNavigate={changePageSizeAndNavigate}
                                />
                            )}
                        </StyledResultsWrapper>
                    </StyledSearchBody>
                </>
            )}
            {searchSubmitted && !shouldShowContent && (
                <StyledThrobber
                    large
                    testid="search-page-throbber"
                />
            )}
        </StyledSearchPage>
    );
};

export default React.memo<SearchPageProps>(SearchPage, isEqual);

const StyledSearchPage = styled.div`
    display: flex;
    position: relative;
    flex-direction: column;
    min-height: 400px;
    padding: 0 20px 64px;
    max-width: ${({ theme }) => theme.breakpoints.pageWidth};
    margin: 0 auto;

    @media (max-width: 1200px) {
        margin-left: 0;
        min-width: 55%;
        max-width: 100%;
        min-height: 300px;
    }
`;

const TabsContainer = styled.div`
    margin-bottom: 16px;
`;

const ResultsRow = styled.div`
    margin-bottom: 16px;
    display: flex;
    align-items: center;
    justify-content: space-between;
`;

const ResultsCountDesktop = styled(H5)<{ $hasSortOptions: boolean }>`
    color: ${({ theme }) => theme.colors.steel};
`;

const FloatingSaveButton = styled.div`
    position: fixed;
    bottom: 20px;
    left: calc(50% - 8px);
    transform: translate(-50%);
    z-index: 500;
    width: 239px;
    padding: 0;
    margin-right: 8px;
`;

const SaveButton = styled.div`
    .searchOptionsSaveButton {
        max-width: 245px;
        width: 100%;
    }
`;

const ResultsCountMobile = styled(BodyPrimary)`
    align-self: center;
    color: ${({ theme }) => theme.colors.grey200};
`;

const StyledSearchBody = styled.div`
    display: flex;
    flex-direction: row;
`;

const StyledResultsWrapper = styled.div`
    width: calc(100% - 306px);
    position: relative;

    @media (max-width: ${({ theme }) => theme.breakpoints.tabletNarrowWidth}) {
        width: 100%;
    }
`;

const StyledThrobber = styled(Throbber)`
    position: absolute;
    right: 0;
    width: 100%;
    height: 100%;
    background-color: ${({ theme }) => theme.colors.shadowOverlay50};
    margin-top: 0;
    z-index: 4;

    @media (min-width: 375px) {
        padding: 100px 0 0;
    }

    @media (min-width: 976px) {
        padding: 200px 0 0;
    }
`;
